import * as XLSX from 'xlsx-js-style';
import { CellObject } from 'xlsx-js-style';
import { MetricGroupsAndPeriods } from '../../../kpi/dto/MetricGroupsAndPeriods';
import {
  formats,
  getCell, getColumnsWidths,
  withAllColumnsWidth,
  withColumnsWithValuesWidth,
  withHeaderHeight,
  withHeaderStyling,
} from '../../utils';
import { Row, Rows } from '../../interfaces';
import { MetricGroup } from '../../../kpi/dto/MetricGroup';
import { Metric } from '../../../kpi/dto/Metric';
import {
  MetricDataProperty,
  MetricDataType,
  MetricSign,
  MetricType,
} from '../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric';
import { MetricData, MetricDataRole } from '../../../kpi/dto/MetricData';
import { MetricCalculationService } from '../../../kpi/service/internal/metric-calculation.service';
import { parseExcelPeriod } from '../../utils/parsing';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { DateService } from '../../../../util/common/date-service/date.service';
import { intToExcelCol } from "excel-column-name";

export class MetricBulkUploadTemplate {
  private readonly headers: string[]; // are adjusted with periods
  private currentRowIndex = 1; // Each group has starting and ending row. Needs for formula calculation.

  constructor(periods: LocalDate[]) {
    this.headers = ['Metric Group', 'Metric', 'Type'].concat(periods.map((period) => DateService.getMonthWithoutDay(period)));
  }

  // New period version is used: YYYY-MM (both import and export).
  export(metricGroups: MetricGroupsAndPeriods, fileName: string): void {
    const book = XLSX.utils.book_new();
    const sheet = this.getSheet(metricGroups);
    XLSX.utils.book_append_sheet(book, sheet);
    XLSX.writeFile(book, fileName);
  }

  /**
   * New period version is used: YYYY-MM (both import and export).
   * This import is used, when we take from the Excel the period data, which we expect to receive.
   * This means, that all non-persisted periods will be ignored.
   * Also, Metrics with type DOCUMENT will be ignored
   * <hr>
   * <p>
   * E.x.:
   * Given we have persisted periods 01-01-2024 and 01-04-2023.
   * | Group name | 01-01-2024 | 01-04-2024 | 01-05-2024 |
   * |------------|------------|------------|------------|
   * | Clients    |    100     |     150    |    160     |
   *
   * <p>
   * Then only this data will be taken into account:
   *
   * | Group name | 01-01-2024 | 01-04-2024 |
   * |------------|------------|------------|
   * | Clients    |    100     |     150    |
   *
   * @param data from Excel
   * @param metricGroups already persisted data which will be overridden.
   * @param role to apply the bulk upload on actual or budget versions.
   */
  import(data: unknown[], metricGroups: MetricGroup[], role: MetricDataRole): void {
    MetricCalculationService.setGroupsVisibility(metricGroups);
    const periods = this.headers.splice(3).map((period) => period + '-01');
    metricGroups.forEach((group) => {
      const groupedData = data.filter((row) => row[this.headers[0]] === group.name);
      group.metrics.filter((metric) => metric.dataProps.dataType !== MetricDataType.DOCUMENT).forEach((metric) => {
        metric.companyMetrics[0].metricData
          .filter((m) => m.role === role && periods.includes(m.period))
          .forEach((metricData) => {
            const row = groupedData.find(
              (grouped) => grouped[this.headers[1]] === metric.name);
            this.setMetricDataValue(row, metric.dataProps, metricData);
          });
      });
    });
  }

  getSheet(ics: MetricGroupsAndPeriods): XLSX.WorkSheet {
    const preparedData = this.prepareMetricGroupsForExcel(ics);
    const sheet = XLSX.utils.aoa_to_sheet(preparedData);
    return withColumnsWithValuesWidth(withHeaderHeight(withAllColumnsWidth(withHeaderStyling(sheet))),
      getColumnsWidths());
  }

  /**
   * Sets the new value from the Excel to the metrics.
   * If the cell could not be found, ignores the value.
   *
   * @param row is an Excel row with metric data and periods
   * @param metricDataProperty of metric with sign (positive/negative/any) and type (number, string...).
   * @param metricData will be updated with new values from the Excel.
   */
  private setMetricDataValue(row: unknown, metricDataProperty: MetricDataProperty, metricData: MetricData): void {
    if (!row) {
      return;
    }
    const periods = Object.keys(row)
      .map((rowCell) => rowCell + '-01')
      .map((rowCell) => parseExcelPeriod(rowCell))
      .filter((periodCell) => periodCell);
    const period = metricData.period.toString();
    if (!periods.includes(period)) {
      return;
    }
    let value = row[DateService.getMonthWithoutDay(period)].toString();
    const isRawValueNumber = metricDataProperty.dataType === MetricDataType.NUMBER;
    const isRawValuePercent = metricDataProperty.dataType === MetricDataType.PERCENT;
    const isRawValueNumeric = isRawValueNumber || isRawValuePercent;
    if (isRawValueNumeric) {
      // value without a thousand separator (commas) and spaces.
      value = value.replace(/,/g, '').trim();
      if (!value || isNaN(Number(value))) {
        metricData.rowValue = null;
        return;
      }
      if (isRawValuePercent) {
        value = Number(value) * 100;
      }
    }

    if (isRawValueNumeric && metricDataProperty.sign === MetricSign.POSITIVE || metricDataProperty.sign === MetricSign.NEGATIVE) {
      value = Math.abs(Math.round(value)).toString();
    }
    if (metricDataProperty.dataType === MetricDataType.DATE) {
      value = parseExcelPeriod(value);
    }
    metricData.rowValue = value;
  }

  // Metrics with type DOCUMENT must be ignored
  private prepareMetricGroupsForExcel(groups: MetricGroupsAndPeriods): Rows {
    let rows: Rows = [this.headers.map((header) => getCell({
      t: 's',
      v: header,
    }))];

    groups.metricGroups.forEach((group) => {
      group.metrics = group.metrics.filter((metric) => !metric.companyMetrics[0].isHidden)
        .filter((metric) => metric.dataProps.dataType !== MetricDataType.DOCUMENT);
      if (group.temp.isSomeMetricsSelected) {
        rows = rows.concat(this.prepareSingleMetricGroup(group));
      }
    });
    return rows;
  }

  private prepareSingleMetricGroup(group: MetricGroup): Rows {
    const groupRows: Rows = [];
    group.metrics.forEach((metric) => {
      if (!metric.companyMetrics[0].isHidden) {
        groupRows.push(this.prepareSingleMetric(metric, group));
      }
    });
    // adds one empty row between the next group.
    this.currentRowIndex++;
    groupRows.push([]);
    return groupRows;
  }

  /**
   * Creates an Excel row.
   * First row values are static, then the dynamic periods are added.
   * Creates fancy-shmency formulas for Metric props type === Starting | Ending.
   *
   * @param metric must contain all periods from group in the 'metricData'.
   * @param group is used for name and amount of metrics.
   */
  private prepareSingleMetric(metric: Metric, group: MetricGroup): Row {
    const row: Row = [group.name, this.prepareMetricNameCell(metric), this.getNameForColumnType(metric.dataProps)];
    const initRowLength = row.length;
    for (let i = initRowLength; i < this.headers.length; i++) {
      const period = this.headers[i] + '-01';
      switch (metric.props.type) {
        case MetricType.STARTING:
          if (i === initRowLength) {
            row.push(this.getMetricValue(metric, period));
          } else {
            const prevColumnIndex = (intToExcelCol(i));
            const endingRowIndex = this.currentRowIndex + group.metrics.length;
            row.push(getCell({
              f: `=${prevColumnIndex + endingRowIndex}`,
              z: formats.thousandsSeparator,
              t: 'n',
              v: this.getMetricValue(metric, period).v,
            }));
          }
          break;
        case MetricType.ENDING:
          const currColumnIndex = (intToExcelCol(i + 1));
          const startingRowIndex = this.currentRowIndex + 2 - group.metrics.length;
          row.push(getCell({
            f: `=SUM(${currColumnIndex + startingRowIndex}:${currColumnIndex + this.currentRowIndex})`,
            z: formats.thousandsSeparator,
            t: 'n',
            v: this.getMetricValue(metric, period).v,
          }));
          break;
        case MetricType.REGULAR:
          row.push(this.getMetricValue(metric, period));
          break;
      }
    }
    this.currentRowIndex++;
    return row;
  }

  private getNameForColumnType(metricProps: MetricDataProperty): string {
    switch (metricProps.dataType) {
      case MetricDataType.STRING:
        return 'Text';
      case MetricDataType.NUMBER:
        switch (metricProps.sign) {
          case MetricSign.POSITIVE:
            return 'Positive number';
          case MetricSign.NEGATIVE:
            return 'Negative number';
          case MetricSign.ANY:
            return 'Any sign number';
          default:
            return 'Undefined';
        }
      case MetricDataType.PERCENT:
        return 'Percentage';
      case MetricDataType.DATE:
        return 'Date';
    }
  }

  /**
   * If the metric name is starting or ending -> makes it bold. Otherwise, is a plain text.
   *
   * @param metric - with the name and type (starting/ending/regular).
   * @returns - The prepared cell with bold style or a plain string.
   */
  private prepareMetricNameCell(metric: Metric): Partial<CellObject> | string {
    switch (metric.props.type) {
      case MetricType.STARTING:
      case MetricType.ENDING:
        return getCell({ v: metric.name, s: { font: { bold: true } } });
      case MetricType.REGULAR:
        return metric.name;
    }
  }

  private getMetricValue(metric: Metric, period: string): Partial<XLSX.CellObject> {
    let value = metric.companyMetrics[0].metricData.find((data) => data.period.toString() === period)?.rowValue ?? undefined;
    if (!value) {
      return this.getCell(null, metric.dataProps.dataType);
    }
    switch (metric.dataProps.sign) {
      case MetricSign.POSITIVE:
        value = Math.abs(Number(value)).toString();
        break;
      case MetricSign.NEGATIVE:
        value = (-Math.abs(Number(value))).toString();
        break;
      case MetricSign.ANY:
        break;
    }
    return this.getCell(value, metric.dataProps.dataType);
  }

  private getCell(value: string | number, type: MetricDataType): Partial<XLSX.CellObject> {
    if (type === MetricDataType.NUMBER) {
      return getCell({ v: value ?? 0, z: formats.thousandsSeparator, t: 'n' });
    }
    if (type === MetricDataType.PERCENT) {
      return getCell({ v: value ? Number(value) / 100 : 0, z: formats.percentsNoSpace, t: 'n' });
    }
    return getCell({ v: value ?? '', t: 's' });
  }
}
